import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { Badge } from '@givebutter/primitives/badge';
import { Button } from '@givebutter/primitives/button';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import { faArrowRightArrowLeft, faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import {
    faArrowUpRightFromSquare,
    faBadgeCheck,
    faBuildingColumns,
    faClock,
    faMobile,
    faReceipt,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { show as showIntercomChat } from '@intercom/messenger-js-sdk';
import { verifyNonProfit } from 'api/verification';
import * as VerificationStep from 'assets/js/pages/account-settings/account-about/VerificationStep';
import * as Form from 'components/form';
import { NonProfitVerificationActionRequired } from 'components/nonprofit-verification';
import { NonProfitVerificationCancelled } from 'components/nonprofit-verification/NonProfitVerificationCancelled';
import { NonProfitVerificationFiles } from 'components/nonprofit-verification/NonProfitVerificationFiles';
import { NonProfitVerificationUnderReview } from 'components/nonprofit-verification/NonProfitVerificationUnderReview';
import * as Tooltip from 'components/tooltip';
import {
    UploadBrowse,
    UploadFile,
    UploadHeadless,
    UploadProvider,
    UseFormUpload,
} from 'components/upload';
import { AnchorHashIds } from 'constants/anchor-hash-ids';
import { NONPROFIT_VERIFICATION_LINK } from 'constants/links';
import { posthogEvents } from 'constants/posthog-events';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentUser } from 'customHooks/useCurrentUser';
import { useInvalidateUserAndAccount } from 'customHooks/useInvalidateUserAndAccount';
import { usePosthog } from 'customHooks/usePosthog';
import { first } from 'lodash';
import { OrganizationType } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface NonProfitVerificationFormValues {
    isRepresentative: boolean;
    ein: string;
    fiscallySponsored: boolean;
    organizationType: OrganizationType;
    fiscallySponsoredProof: UploadFile[];
}

const NonProfitVerification = () => {
    const toast = useToast();
    const { accountId, legalEntity, sponsoringLegalEntity, sponsorshipStatus } =
        useCurrentAccount();
    const { id: userId, isEmailVerified } = useCurrentUser();
    const { mutateAsync: invalidateUserData } = useInvalidateUserAndAccount();

    const expired = legalEntity?.status === 'cancelled' && !legalEntity?.verification_status_reason;

    const [isFormVisible, setIsFormVisible] = useState(expired);

    const methods = Form.useGivebutterForm<NonProfitVerificationFormValues>({
        defaultValues: {
            isRepresentative: false,
            ein: legalEntity?.ein ?? '',
            fiscallySponsored: false,
            organizationType: 'registered',
            fiscallySponsoredProof: [],
        },
    });
    const { control, path, watch, setError, handleSubmit, reset, getValues } = methods;

    const [isRepresentative, organizationType] = watch(['isRepresentative', 'organizationType']);

    const { pushEvent } = usePosthog();

    const { mutate: submit, isPending: isLoading } = useMutation<
        unknown,
        GivebutterAxiosError,
        NonProfitVerificationFormValues
    >({
        mutationFn: values => {
            const data = {
                ein: values.ein,
                account_id: accountId.toString(),
                fiscally_sponsored: values.organizationType === 'fiscally_sponsored' ? '1' : '0',
                fiscally_sponsored_proof: first(getValues('fiscallySponsoredProof'))?.file as File,
            };

            return verifyNonProfit(userId, data);
        },
        onSuccess: async () => {
            try {
                await invalidateUserData();
                setIsFormVisible(false);
                reset();
            } catch (e) {
                toast.error('Something went wrong. Please try again.');
            }
        },
        onError: (e: GivebutterAxiosError) => {
            const data = e.response?.data;
            const errors = data?.errors;

            toast.error(data?.message ?? 'Something went wrong. Please try again.');

            if (typeof errors === 'object') {
                for (let field in errors) {
                    setError(field as keyof NonProfitVerificationFormValues, {
                        message: errors[field][0],
                    });
                }
            }
        },
    });

    return (
        <VerificationStep.Root
            isCompleted={
                legalEntity?.status === 'approved' ||
                (['approved', 'unclaimed'].includes(sponsoringLegalEntity?.status ?? '') &&
                    sponsorshipStatus === 'approved')
            }
        >
            <VerificationStep.Heading id={AnchorHashIds.VerifyNonprofit} className="scroll-mt-24">
                <VerificationStep.HeadingIcon />
                <VerificationStep.HeadingContent>
                    <VerificationStep.HeadingStep>Step 3</VerificationStep.HeadingStep>
                    <VerificationStep.HeadingTitle>
                        Verify your nonprofit
                        <VerificationStep.HeadingSubtitle>
                            {legalEntity && !sponsoringLegalEntity
                                ? `${legalEntity.name} ${legalEntity.ein}`
                                : null}
                            {!legalEntity &&
                            sponsoringLegalEntity &&
                            ['approved', 'unclaimed'].includes(sponsoringLegalEntity?.status ?? '')
                                ? `${sponsoringLegalEntity.name} ${sponsoringLegalEntity.ein}`
                                : null}
                        </VerificationStep.HeadingSubtitle>
                    </VerificationStep.HeadingTitle>
                </VerificationStep.HeadingContent>
                <VerificationStep.HeadingLabel>
                    <Badge size="sm">Optional</Badge>
                </VerificationStep.HeadingLabel>
            </VerificationStep.Heading>

            <VerificationStep.Content>
                <p className="text-sm text-gray-700">
                    Are you a registered nonprofit? Complete this verification to claim and verify
                    your nonprofit.{' '}
                    <a target="_blank" rel="noopener noreferrer" href={NONPROFIT_VERIFICATION_LINK}>
                        Learn more <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                    </a>
                </p>

                {legalEntity && (
                    <>
                        {legalEntity.status === 'action_required' && (
                            <>
                                <Separator />

                                <NonProfitVerificationActionRequired />
                            </>
                        )}

                        {legalEntity.status === 'under_review' && (
                            <NonProfitVerificationUnderReview />
                        )}

                        {legalEntity.status === 'cancelled' && <NonProfitVerificationCancelled />}

                        {legalEntity.status === 'denied' && (
                            <>
                                <Separator />

                                <AlertNotification.Root type="error">
                                    <AlertNotification.Icon />
                                    <AlertNotification.Content>
                                        <AlertNotification.Title>
                                            Ineligible for verification
                                        </AlertNotification.Title>
                                        <AlertNotification.Description>
                                            After further review, we have deemed your nonprofit
                                            ineligible to be verified on Givebutter. If you believe
                                            this is in error, please reach out to{' '}
                                            <a
                                                href={`mailto:${import.meta.env.VITE_DASHBOARD_SAFETY_TEAM_EMAIL}`}
                                            >
                                                {import.meta.env.VITE_DASHBOARD_SAFETY_TEAM_EMAIL}
                                            </a>
                                        </AlertNotification.Description>
                                    </AlertNotification.Content>
                                </AlertNotification.Root>
                            </>
                        )}

                        {['action_required', 'under_review'].includes(legalEntity.status) &&
                            !!legalEntity.file_count && <NonProfitVerificationFiles />}
                    </>
                )}

                {(!legalEntity || expired) && (
                    <div className="w-full space-y-3 rounded-lg bg-gray-50 p-4">
                        <p className="font-medium text-gray-700">
                            Verify your nonprofit to unlock these benefits
                        </p>

                        <ul className="grid grid-cols-2 gap-x-4 gap-y-2">
                            <li className="flex items-center gap-2">
                                <FontAwesomeIcon
                                    icon={faClock}
                                    size="sm"
                                    className="mx-auto text-gray-400"
                                />
                                <span className="flex-1">
                                    Funds Raised Available Faster (48 hours)
                                </span>
                            </li>
                            <li className="flex items-center gap-2">
                                <FontAwesomeIcon
                                    icon={faBuildingColumns}
                                    size="sm"
                                    className="mx-auto text-gray-400"
                                />
                                <span className="flex-1">Accept ACH & DAF payments</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <FontAwesomeIcon
                                    icon={faBadgeCheck}
                                    size="sm"
                                    className="mx-auto text-gray-400"
                                />
                                <span className="flex-1">
                                    Verification badge on your campaign pages
                                </span>
                            </li>
                            <li className="flex items-center gap-2">
                                <FontAwesomeIcon
                                    icon={faMobile}
                                    size="sm"
                                    className="mx-auto text-gray-400"
                                />
                                <span className="flex-1">
                                    Outbound texting (requires{' '}
                                    <Link to={`/accounts/${accountId}/settings/about/subscription`}>
                                        Givebutter Plus
                                    </Link>
                                    )
                                </span>
                            </li>
                            <li className="flex items-center gap-2">
                                <FontAwesomeIcon
                                    icon={faReceipt}
                                    size="sm"
                                    className="mx-auto text-gray-400"
                                />
                                <span className="flex-1">
                                    EIN number displayed on your email receipts
                                </span>
                            </li>
                            <li className="flex items-center gap-2">
                                <FontAwesomeIcon
                                    icon={faArrowRightArrowLeft}
                                    size="sm"
                                    className="mx-auto text-gray-400"
                                />
                                <span className="flex-1">Automated payouts</span>
                            </li>
                        </ul>
                    </div>
                )}

                {sponsoringLegalEntity && (
                    <>
                        {sponsorshipStatus === 'pending' && (
                            <>
                                <Separator />
                                <NonProfitVerificationUnderReview />
                                <NonProfitVerificationFiles />
                            </>
                        )}

                        {sponsorshipStatus === 'denied' && (
                            <>
                                <Separator />
                                <AlertNotification.Root type="error">
                                    <AlertNotification.Icon />
                                    <AlertNotification.Content>
                                        <AlertNotification.Title>
                                            Your fiscal sponsorship was denied.
                                        </AlertNotification.Title>
                                        <AlertNotification.Description>
                                            Sorry, but we are unable to verify your fiscal
                                            sponsorship. If you believe this is a mistake, please{' '}
                                            <Button
                                                variant="none"
                                                color="primary"
                                                className="font-medium"
                                                onClick={showIntercomChat}
                                            >
                                                contact us
                                            </Button>
                                            .
                                        </AlertNotification.Description>
                                    </AlertNotification.Content>
                                </AlertNotification.Root>
                            </>
                        )}
                    </>
                )}

                <Form.Provider {...methods}>
                    {!isFormVisible &&
                    (!legalEntity || legalEntity.status === 'cancelled') &&
                    !sponsoringLegalEntity ? (
                        <div className="flex flex-col gap-4">
                            {!isEmailVerified && (
                                <AlertNotification.Root type="info">
                                    <AlertNotification.Icon />
                                    <AlertNotification.Content>
                                        <AlertNotification.Title>
                                            You need to{' '}
                                            <Button
                                                variant="none"
                                                color="primary"
                                                onClick={() =>
                                                    window.scrollTo({
                                                        top: 100,
                                                        behavior: 'smooth',
                                                    })
                                                }
                                            >
                                                verify your email address
                                            </Button>{' '}
                                            before you can proceed.
                                        </AlertNotification.Title>
                                    </AlertNotification.Content>
                                </AlertNotification.Root>
                            )}

                            <Form.Container className="flex flex-row gap-2">
                                <Form.UseFormCheckboxInput
                                    control={control}
                                    name={path('isRepresentative')}
                                    className="mt-1"
                                />
                                <Form.Label name={path('isRepresentative')}>
                                    I am a legal or authorized representative of the nonprofit I
                                    would like to verify on Givebutter
                                </Form.Label>
                            </Form.Container>

                            <Form.Container className="items-start">
                                <Button
                                    variant="solid"
                                    onClick={e => {
                                        e.preventDefault();
                                        setIsFormVisible(true);
                                        !legalEntity &&
                                            pushEvent(
                                                posthogEvents.ACCOUNT_VERIFY_NONPROFIT_STARTED,
                                            );
                                    }}
                                    disabled={!isEmailVerified || !isRepresentative}
                                >
                                    {!legalEntity || expired ? 'Get started' : 'Edit EIN'}
                                </Button>
                            </Form.Container>
                        </div>
                    ) : null}

                    {isFormVisible ? (
                        <form
                            onSubmit={handleSubmit(values => submit(values))}
                            className="flex flex-col gap-4"
                        >
                            <Form.Container>
                                <Form.UseFormRadioGroup
                                    name={path('organizationType')}
                                    control={control}
                                    orientation={'vertical'}
                                    rules={{ required: 'Organization type is required' }}
                                    variant={'card'}
                                >
                                    <RadioGroup.Item
                                        id={'registered'}
                                        value={'registered'}
                                        className={'flex gap-x-3'}
                                        size={'sm'}
                                    >
                                        <RadioGroup.IndicatorContainer>
                                            <RadioGroup.Indicator />
                                        </RadioGroup.IndicatorContainer>
                                        <div className={'flex flex-1 flex-col'}>
                                            <Form.Label
                                                name={path('organizationType')}
                                                className={'text-base'}
                                                htmlFor={'registered'}
                                            >
                                                Registered nonprofit
                                            </Form.Label>
                                            <p className={'text-base text-gray-600'}>
                                                My organization is registered directly with the IRS
                                                and has its own tax-exempt EIN.
                                            </p>
                                        </div>
                                    </RadioGroup.Item>
                                    <RadioGroup.Item
                                        id={'fiscally_sponsored'}
                                        value={'fiscally_sponsored'}
                                        className={'flex gap-x-3'}
                                        size={'sm'}
                                    >
                                        <RadioGroup.IndicatorContainer>
                                            <RadioGroup.Indicator />
                                        </RadioGroup.IndicatorContainer>
                                        <div className={'flex flex-1 flex-col'}>
                                            <Form.Label
                                                name={path('organizationType')}
                                                className={'text-base'}
                                                htmlFor={'fiscally_sponsored'}
                                            >
                                                Group exempt or sponsored
                                            </Form.Label>
                                            <p className={'text-base text-gray-600'}>
                                                My organization is fiscally sponsored, a chapter, or
                                                has a parent organization that shares or assigned
                                                our EIN.
                                            </p>
                                        </div>
                                    </RadioGroup.Item>
                                </Form.UseFormRadioGroup>
                                <Form.Error name={path('organizationType')} />
                            </Form.Container>

                            {organizationType === 'fiscally_sponsored' && (
                                <div className="flex flex-col gap-4">
                                    <AlertNotification.Root type="info">
                                        <AlertNotification.Icon />
                                        <AlertNotification.Content>
                                            <AlertNotification.Title>
                                                Note about benefits
                                            </AlertNotification.Title>
                                            <AlertNotification.Description>
                                                Organizations with shared tax-exempt EINs are not
                                                currently eligible to accept ACH and DAF payments or
                                                to have funds available for withdrawal faster (still
                                                72 hours). Additionally, due to federal guidelines,
                                                outbound text messaging is unavailable.
                                            </AlertNotification.Description>
                                        </AlertNotification.Content>
                                    </AlertNotification.Root>
                                    <div className="flex flex-col gap-0">
                                        <p className="text-lg font-medium">
                                            Group exempt or sponsored documentation
                                        </p>
                                        <p className="text-gray-600">
                                            Please upload documentation that proves your
                                            relationship to the parent organization that allows you
                                            to use this EIN. For example, group exemption or
                                            membership confirmation notices.
                                        </p>
                                    </div>
                                    <Form.Container className="md:max-w-md">
                                        <Form.Label name={path('fiscallySponsoredProof')}>
                                            Documentation{' '}
                                            <Tooltip.Root>
                                                <Tooltip.Trigger
                                                    asChild={true}
                                                    className={`cursor-help text-gray-500`}
                                                >
                                                    <FontAwesomeIcon icon={faQuestionCircle} />
                                                </Tooltip.Trigger>
                                                <Tooltip.Portal>
                                                    <Tooltip.Content inverse>
                                                        <div className="flex flex-col gap-1">
                                                            <p>Maximum upload file size: 5MB</p>
                                                            <p>
                                                                Accepted file types: .doc, .docx,
                                                                .pdf, .png, .jpg, .jpeg
                                                            </p>
                                                        </div>
                                                        <Tooltip.Arrow inverse />
                                                    </Tooltip.Content>
                                                </Tooltip.Portal>
                                            </Tooltip.Root>
                                        </Form.Label>
                                        <UploadProvider>
                                            <UploadHeadless>
                                                <UseFormUpload
                                                    control={control}
                                                    variant={'headless'}
                                                    name={'fiscallySponsoredProof'}
                                                    allowMultiple={false}
                                                    allowReorder={false}
                                                    strategy={'memory'}
                                                    maxFileSize={'5MB'}
                                                    acceptedFileTypes={[
                                                        'image/*',
                                                        'application/msword',
                                                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                                                        'application/pdf',
                                                    ]}
                                                />
                                            </UploadHeadless>
                                            <UploadBrowse />
                                        </UploadProvider>
                                        <Form.UseFormError name={path('fiscallySponsoredProof')} />
                                    </Form.Container>
                                </div>
                            )}

                            {organizationType && (
                                <Form.Container>
                                    <Form.Label name={path('ein')}>
                                        {organizationType === 'registered'
                                            ? 'EIN'
                                            : 'Parent or shared EIN'}
                                    </Form.Label>
                                    <Form.TextInputRoot
                                        name={path('ein')}
                                        control={control}
                                        rules={{ required: 'EIN is required' }}
                                        inputRootProps={{
                                            className: 'max-w-sm',
                                        }}
                                    >
                                        <Form.TextInputForm placeholder="12-3456789" autoFocus />
                                    </Form.TextInputRoot>
                                    <Form.Error name={path('ein')} />
                                </Form.Container>
                            )}

                            <Form.Container className="flex items-start">
                                <Form.SubmitButton isSubmitting={isLoading}>
                                    Submit
                                </Form.SubmitButton>
                            </Form.Container>
                        </form>
                    ) : null}
                </Form.Provider>
            </VerificationStep.Content>
        </VerificationStep.Root>
    );
};

export { NonProfitVerification };
